import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutTemplate, Plus, Pencil, Trash2, Loader2, Save, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { DOC_CATEGORIES, RIGHT_DEFS, CADENCES } from "@/lib/rights";

const EMPTY = {
  name: "",
  description: "",
  tier: "Standard",
  reporting_cadence: "monthly",
  allowed_categories: ["Report", "Financials"],
  rights: RIGHT_DEFS.reduce((acc, r) => ({ ...acc, [r.k]: false }), { monthly_updates: true }),
  notes: "",
};

export const RightsTemplatesDialog = ({ open, onOpenChange, companyId }) => {
  const [data, setData] = useState({ presets: [], custom: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view; object = form
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/companies/${companyId}/rights-templates`); setData(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setEditing(null); load(); } }, [open, companyId]);

  const startCreate = () => setEditing({ ...EMPTY, allowed_categories: [...EMPTY.allowed_categories], rights: { ...EMPTY.rights } });
  const startEdit = (t) => setEditing({
    id: t.id,
    name: t.name,
    description: t.description || "",
    tier: t.rights_payload?.tier || "Standard",
    reporting_cadence: t.rights_payload?.reporting_cadence || "monthly",
    allowed_categories: [...(t.rights_payload?.allowed_categories || [])],
    rights: { ...(t.rights_payload?.rights || {}) },
    notes: t.rights_payload?.notes || "",
  });

  const setF = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const toggleCat = (c) => setEditing((e) => ({ ...e, allowed_categories: e.allowed_categories.includes(c) ? e.allowed_categories.filter((x) => x !== c) : [...e.allowed_categories, c] }));
  const toggleRight = (k, v) => setEditing((e) => ({ ...e, rights: { ...e.rights, [k]: v } }));

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Template name is required");
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/rights-templates/${editing.id}`, editing);
        toast.success("Template updated");
      } else {
        await api.post(`/companies/${companyId}/rights-templates`, editing);
        toast.success("Template created");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/rights-templates/${id}`); toast.success("Template deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-primary" /> Rights Templates</DialogTitle>
        </DialogHeader>

        {editing ? (
          <div className="space-y-5" data-testid="rights-template-form">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label>Template name</Label><Input value={editing.name} onChange={(e) => setF("name", e.target.value)} placeholder="e.g. Seed Angels" className="mt-1" data-testid="template-name" /></div>
              <div><Label>Tier / class label</Label><Input value={editing.tier} onChange={(e) => setF("tier", e.target.value)} className="mt-1" data-testid="template-tier" /></div>
            </div>
            <div><Label>Description (internal)</Label><Input value={editing.description} onChange={(e) => setF("description", e.target.value)} placeholder="Short note for your team" className="mt-1" data-testid="template-description" /></div>
            <div>
              <Label>Reporting cadence</Label>
              <Select value={editing.reporting_cadence} onValueChange={(v) => setF("reporting_cadence", v)}>
                <SelectTrigger className="mt-1" data-testid="template-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>{CADENCES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Allowed document categories</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {DOC_CATEGORIES.map((c) => (
                  <label key={c} className="flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 text-sm hover:bg-secondary">
                    <Checkbox checked={editing.allowed_categories.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`template-cat-${c}`} />{c}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Specific rights</Label>
              <div className="mt-2 space-y-1.5">
                {RIGHT_DEFS.map((rd) => (
                  <div key={rd.k} className="flex items-center justify-between rounded-lg border px-3 py-1.5">
                    <span className="text-sm">{rd.label}</span>
                    <Switch checked={!!editing.rights?.[rd.k]} onCheckedChange={(v) => toggleRight(rd.k, v)} data-testid={`template-flag-${rd.k}`} />
                  </div>
                ))}
              </div>
            </div>
            <div><Label>Notes (shown to investor)</Label><Textarea value={editing.notes} onChange={(e) => setF("notes", e.target.value)} className="mt-1" data-testid="template-notes" /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Back</Button>
              <Button onClick={save} disabled={saving} className="gap-2" data-testid="template-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save template</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Reusable rights profiles you can apply to investors in one click.</p>
              <Button size="sm" className="gap-1.5" onClick={startCreate} data-testid="template-create"><Plus className="h-4 w-4" /> New template</Button>
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Built-in presets</div>
              <div className="space-y-2">
                {data.presets.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg border bg-card p-3" data-testid={`preset-row-${t.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2"><span className="text-sm font-semibold">{t.name}</span><Badge variant="secondary" className="gap-1 font-normal"><Lock className="h-3 w-3" /> preset</Badge></div>
                      <p className="truncate text-xs text-muted-foreground">{t.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your templates</div>
              {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : data.custom.length === 0 ? (
                <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">No custom templates yet. Create one to standardize onboarding.</p>
              ) : (
                <div className="space-y-2">
                  {data.custom.map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg border bg-card p-3" data-testid={`custom-row-${t.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">{t.name}</div>
                        <p className="truncate text-xs text-muted-foreground">{t.description || t.rights_payload?.notes}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
